import { Box } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import ActionDrawer, { UI, btnSx } from "../components/ActionDrawer";
import {
  CardGrid,
  Empty,
  FilterChips,
  GridCard,
  PageHead,
  SearchBox,
  Tag,
  Toolbar,
} from "../components/DashTable";
import PageLoader from "../components/Loader";
import { useToast } from "../components/Toast";
import apiService from "../Services/api.service";

const shortDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
};

const PAGE_SIZE = 15;

const STATUS_FILTERS = [
  { value: "", label: "All" },
  { value: "pending", label: "Pending" },
  { value: "submitted", label: "Submitted" },
];

/* TC5. One checklist per installation. The warranty card is blocked until
   it is submitted with photos. */
const Checklists = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const token = localStorage.getItem("token");

  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [pagination, setPagination] = useState(null);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [active, setActive] = useState(null);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const searchMounted = useRef(false);
  const statusMounted = useRef(false);

  const fetchRows = async (targetPage = page, targetSearch = search, targetStatus = status) => {
    setIsLoading(true);
    setLoadFailed(false);
    try {
      const res = await apiService.fetchChecklists(token, {
        page: targetPage,
        limit: PAGE_SIZE,
        search: targetSearch,
        status: targetStatus,
      });
      setRows(res.data || []);
      setSummary(res.summary || null);
      setPagination(res.pagination || null);
    } catch (error) {
      setLoadFailed(true);
      toast.error("Error while fetching checklists");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchRows(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  // Debounced so every keystroke doesn't fire a request; resets to page 1
  // since a filtered result set has its own page count. Skips its first run
  // so mount doesn't double-fetch alongside the [page] effect above.
  useEffect(() => {
    if (!searchMounted.current) {
      searchMounted.current = true;
      return;
    }
    const t = setTimeout(() => {
      if (page === 1) fetchRows(1, search, status);
      else setPage(1);
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  // Chip click, so no debounce needed here. Same first-run skip as above.
  useEffect(() => {
    if (!statusMounted.current) {
      statusMounted.current = true;
      return;
    }
    if (page === 1) fetchRows(1, search, status);
    else setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const kpis = summary
    ? [
        { label: "Pending", value: summary.pending, alarm: true },
        { label: "Submitted", value: summary.submitted },
      ]
    : [];

  return (
    <Box sx={{ padding: { xs: "18px 16px 50px", md: "24px 28px 60px" }, maxWidth: 1240 }}>
      <PageHead
        title="Checklists"
        lede="One per installation. The warranty card is blocked until submission."
        kpis={kpis}
      />

      <Toolbar
        right={
          <SearchBox
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by clinic or location"
            width={320}
          />
        }
      >
        <FilterChips options={STATUS_FILTERS} value={status} onChange={setStatus} />
      </Toolbar>

      {loadFailed ? (
        <Empty
          title="Could not load checklists"
          sub={
            <Box
              component="button"
              type="button"
              onClick={fetchRows}
              sx={{ ...btnSx("ghost"), margin: "12px auto 0", display: "block" }}
            >
              Retry
            </Box>
          }
        />
      ) : rows.length === 0 && !isLoading ? (
        <Empty
          title={search ? "No matching checklists" : "No checklists yet"}
          sub={
            search
              ? "Try a different clinic name or location."
              : "One is created when you complete an inauguration."
          }
        />
      ) : (
        <CardGrid>
          {rows.map((row) => {
            const pending = !row.submittedAt;
            return (
              <GridCard
                key={row.orderId}
                title={row.clinicName}
                tag={
                  pending ? (
                    <Tag tone="b">Pending</Tag>
                  ) : (
                    <Tag tone="g">Submitted</Tag>
                  )
                }
                lines={
                  <>
                    {[row.city, row.state].filter(Boolean).join(", ") || "—"}
                    <br />
                    Installed {shortDate(row.installedAt)}
                    <br />
                    {row.itemsDone ?? 0} of {row.itemsOf ?? 6} items complete
                  </>
                }
                actions={
                  pending ? (
                    <Box
                      component="button"
                      type="button"
                      onClick={() =>
                        setActive({
                          id: "TC5",
                          screenKey: "checklist",
                          orderId: row.orderId,
                          orderNumber: row.orderNumber,
                          clinicName: row.clinicName,
                          context: {},
                        })
                      }
                      sx={{ ...btnSx("ghost"), width: "100%", minHeight: "40px" }}
                    >
                      Continue
                    </Box>
                  ) : (
                    <Box
                      component="button"
                      type="button"
                      onClick={() => navigate(`/dashboard/orders/${row.orderId}`)}
                      sx={{ ...btnSx("ghost"), width: "100%", minHeight: "40px" }}
                    >
                      View
                    </Box>
                  )
                }
              />
            );
          })}
        </CardGrid>
      )}

      {pagination && pagination.total > 0 && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "10px",
            marginBottom: "10px",
            fontSize: 13,
            color: UI.ink3,
          }}
        >
          <span>
            {(pagination.page - 1) * pagination.limit + 1}–
            {Math.min(pagination.page * pagination.limit, pagination.total)} of{" "}
            {pagination.total}
          </span>
          <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <span>
              Page {pagination.page} of {pagination.totalPages}
            </span>
            <Box sx={{ display: "flex", gap: "8px" }}>
              <Box
                component="button"
                type="button"
                onClick={() => setPage((p) => Math.max(p - 1, 1))}
                disabled={pagination.page <= 1}
                sx={btnSx("ghost", pagination.page <= 1)}
              >
                Previous
              </Box>
              <Box
                component="button"
                type="button"
                onClick={() => setPage((p) => Math.min(p + 1, pagination.totalPages))}
                disabled={pagination.page >= pagination.totalPages}
                sx={btnSx("ghost", pagination.page >= pagination.totalPages)}
              >
                Next
              </Box>
            </Box>
          </Box>
        </Box>
      )}

      <ActionDrawer
        open={Boolean(active)}
        screenKey={active?.screenKey}
        item={active}
        onClose={() => setActive(null)}
        onSaved={() => {
          setActive(null);
          fetchRows();
        }}
      />

      {isLoading && <PageLoader />}
    </Box>
  );
};

export default Checklists;
